import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Select,
  MenuItem,
  Chip,
  Paper
} from '@mui/material';
import {
  Info as InfoIcon
} from '@mui/icons-material';

const CareerContext = ({ formData, updateFormData, onValidationChange }) => {
  const [timeline, setTimeline] = useState(formData.timeline || 'flexible');
  const [learningStyle, setLearningStyle] = useState(formData.learningStyle || 'self-paced');
  const [hoursPerWeek, setHoursPerWeek] = useState(formData.hoursPerWeek || '10-15');
  const [additionalNotes, setAdditionalNotes] = useState(formData.additionalNotes || '');

  // This step is optional, so it's always valid
  useEffect(() => {
    onValidationChange(true);
  }, [onValidationChange]);

  // Update form data when any field changes
  useEffect(() => {
    updateFormData({
      timeline,
      learningStyle,
      hoursPerWeek,
      additionalNotes
    });
  }, [timeline, learningStyle, hoursPerWeek, additionalNotes, updateFormData]);

  return (
    <Box>
      {/* Header */}
      <Typography variant="h6" sx={{ color: '#fafafa', mb: 1, fontWeight: 600 }}>
        Additional Context (Optional)
      </Typography>
      <Typography variant="body2" sx={{ color: '#a1a1aa', mb: 1 }}>
        Help us tailor your career plan to your specific situation and preferences
      </Typography>

      <Paper
        sx={{
          p: 2,
          mb: 4,
          backgroundColor: '#0a0a0a',
          border: '1px solid rgba(157,0,255,0.2)'
        }}
      >
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <InfoIcon sx={{ color: '#9D00FF', fontSize: 20, mt: 0.2 }} />
          <Typography variant="caption" sx={{ color: '#a1a1aa' }}>
            While optional, providing this information helps us create a more realistic and actionable plan
            that fits your timeline and resources.
          </Typography>
        </Box>
      </Paper>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {/* Timeline */}
        <FormControl>
          <FormLabel 
            sx={{ 
              color: '#fafafa', 
              fontWeight: 600,
              mb: 2,
              '&.Mui-focused': { color: '#00FFF5' }
            }}
          >
            What's your timeline for this career move?
          </FormLabel>
          <RadioGroup value={timeline} onChange={(e) => setTimeline(e.target.value)}>
            <FormControlLabel 
              value="urgent" 
              control={<Radio sx={{ color: '#525252', '&.Mui-checked': { color: '#00FFF5' } }} />}
              label={
                <Box>
                  <Typography variant="body2" sx={{ color: '#fafafa' }}>
                    Short-term (0-3 months)
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#737373' }}>
                    Need to transition as soon as possible
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel 
              value="flexible" 
              control={<Radio sx={{ color: '#525252', '&.Mui-checked': { color: '#00FFF5' } }} />}
              label={
                <Box>
                  <Typography variant="body2" sx={{ color: '#fafafa' }}>
                    Mid-term (3-12 months)
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#737373' }}>
                    Want to be ready when the right opportunity comes
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel 
              value="longterm" 
              control={<Radio sx={{ color: '#525252', '&.Mui-checked': { color: '#00FFF5' } }} />}
              label={
                <Box>
                  <Typography variant="body2" sx={{ color: '#fafafa' }}>
                    Long-term (12+ months)
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#737373' }}>
                    Planning ahead, building toward a future goal
                  </Typography>
                </Box>
              }
            />
          </RadioGroup>
        </FormControl>

        {/* Learning Style */}
        <FormControl fullWidth>
          <FormLabel 
            sx={{ 
              color: '#fafafa', 
              fontWeight: 600,
              mb: 2
            }}
          >
            Preferred learning style
          </FormLabel>
          <Select
            value={learningStyle}
            onChange={(e) => setLearningStyle(e.target.value)}
            sx={{
              backgroundColor: '#0a0a0a',
              color: '#fafafa',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#262626'
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#525252'
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#00FFF5'
              }
            }}
          >
            <MenuItem value="self-paced">Self-paced online courses (Udemy, Coursera)</MenuItem>
            <MenuItem value="structured">Structured programs with deadlines</MenuItem>
            <MenuItem value="bootcamp">Intensive bootcamps (3-6 months)</MenuItem>
            <MenuItem value="degree">Formal degree programs</MenuItem>
            <MenuItem value="hands-on">Hands-on/project-based learning</MenuItem>
          </Select>
        </FormControl>

        {/* Hours Per Week */}
        <FormControl>
          <FormLabel 
            sx={{ 
              color: '#fafafa', 
              fontWeight: 600,
              mb: 2,
              '&.Mui-focused': { color: '#00FFF5' }
            }}
          >
            How many hours per week can you dedicate to training?
          </FormLabel>
          <RadioGroup value={hoursPerWeek} onChange={(e) => setHoursPerWeek(e.target.value)}>
            <FormControlLabel 
              value="1-5" 
              control={<Radio sx={{ color: '#525252', '&.Mui-checked': { color: '#00FFF5' } }} />}
              label={
                <Box>
                  <Typography variant="body2" sx={{ color: '#fafafa' }}>
                    1-5 hours/week
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#737373' }}>
                    Light commitment - fits around a busy schedule
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel 
              value="5-10" 
              control={<Radio sx={{ color: '#525252', '&.Mui-checked': { color: '#00FFF5' } }} />}
              label={
                <Box>
                  <Typography variant="body2" sx={{ color: '#fafafa' }}>
                    5-10 hours/week
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#737373' }}>
                    Moderate - about 1-2 hours per day
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel 
              value="10-15" 
              control={<Radio sx={{ color: '#525252', '&.Mui-checked': { color: '#00FFF5' } }} />}
              label={
                <Box>
                  <Typography variant="body2" sx={{ color: '#fafafa' }}>
                    10-15 hours/week
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#737373' }}>
                    Dedicated - equivalent to a part-time commitment
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel 
              value="15-25" 
              control={<Radio sx={{ color: '#525252', '&.Mui-checked': { color: '#00FFF5' } }} />}
              label={
                <Box>
                  <Typography variant="body2" sx={{ color: '#fafafa' }}>
                    15-25 hours/week
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#737373' }}>
                    Intensive - serious career transition mode
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel 
              value="25+" 
              control={<Radio sx={{ color: '#525252', '&.Mui-checked': { color: '#00FFF5' } }} />}
              label={
                <Box>
                  <Typography variant="body2" sx={{ color: '#fafafa' }}>
                    25+ hours/week
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#737373' }}>
                    Full-time focus - not currently employed or dedicated sabbatical
                  </Typography>
                </Box>
              }
            />
          </RadioGroup>
          <Typography variant="caption" sx={{ color: '#9D00FF', mt: 1, display: 'block' }}>
            This affects how quickly you can complete certifications and training
          </Typography>
        </FormControl>

        {/* Additional Notes */}
        <FormControl fullWidth>
          <FormLabel 
            sx={{ 
              color: '#fafafa', 
              fontWeight: 600,
              mb: 2
            }}
          >
            Any other context we should know?
          </FormLabel>
          <TextField
            multiline
            rows={4}
            placeholder="E.g., industry preferences, salary expectations, family considerations, location constraints, specific companies you're targeting..."
            value={additionalNotes}
            onChange={(e) => setAdditionalNotes(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#0a0a0a',
                '& fieldset': {
                  borderColor: '#262626'
                },
                '&:hover fieldset': {
                  borderColor: '#525252'
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#00FFF5'
                }
              },
              '& .MuiInputBase-input': {
                color: '#fafafa'
              }
            }}
          />
        </FormControl>
      </Box>

      {/* Summary Chips */}
      <Paper
        sx={{
          mt: 4,
          p: 3,
          backgroundColor: '#0a0a0a',
          border: '1px solid rgba(0,255,245,0.2)'
        }}
      >
        <Typography variant="caption" sx={{ color: '#737373', mb: 2, display: 'block' }}>
          Your preferences:
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Chip 
            label={`Timeline: ${timeline === 'urgent' ? 'Short-term (0-3mo)' : timeline === 'flexible' ? 'Mid-term (3-12mo)' : 'Long-term (12+mo)'}`} 
            size="small"
            sx={{ backgroundColor: '#171717', color: '#00FFF5' }}
          />
          <Chip 
            label={`Learning: ${learningStyle.replace('-', ' ')}`} 
            size="small"
            sx={{ backgroundColor: '#171717', color: '#00FFF5' }}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default CareerContext;

